import * as React from 'react';
import { useTheme } from '@mui/material/styles';
import Stack, { StackProps } from '@mui/material/Stack';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Tooltip from '@mui/material/Tooltip';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

import {
  SpotAtmosphere,
  SpotType,
  GetEventDetailedQuery,
  GetSpotDetailedQuery,
  SpotMusicalGenreEnum,
  EventStatusEnum,
} from '@graphql';
import { getTrueKeys, getSpotOpenStatus } from '@utils';

interface TileProps extends StackProps {
  children: React.ReactNode;
}

interface DateTileProps extends StackProps {
  from: string;
  to: string;
  previousFrom?: string;
  previousTo?: string;
  status?: EventStatusEnum;
  previousStatus?: EventStatusEnum;
}
interface SchedulesTileProps extends StackProps {
  opening: GetSpotDetailedQuery['spot']['opening'];
  closing: GetSpotDetailedQuery['spot']['closing'];
}

interface ScheduleTileProps extends StackProps {
  from: string;
  to: string;
  previousFrom?: string;
  previousTo?: string;
  status?: EventStatusEnum;
  previousStatus?: EventStatusEnum;
}

interface AddressTileProps extends StackProps {
  label?: string;
}

interface SpotTypeTileProps extends StackProps {
  types: SpotType;
}

interface EventPriceTileProps extends StackProps {
  tickets: GetEventDetailedQuery['event']['tickets'];
}

interface SpotPricingTileProps extends StackProps {
  pricing: GetSpotDetailedQuery['spot']['configuration']['pricing'];
}

interface SpotTileProps extends StackProps {
  avatar?: string;
  name: string;
  slug: string;
}

interface CapacityTileProps extends StackProps {
  max?: number | null;
}

interface CloseSpaceTileProps extends StackProps {
  value?: number;
}
interface AtmosphereTileProps extends StackProps {
  atmosphere?: Partial<SpotAtmosphere>;
}
interface MusicalGenreTileProps extends StackProps {
  type?: SpotMusicalGenreEnum | null;
}

const Tile = ({ children, ...rest }: TileProps) => {
  const theme = useTheme();

  return (
    <Stack
      direction="row"
      width="auto"
      p={1}
      borderRadius={theme.spacing(1)}
      spacing={1}
      flex={1}
      alignItems="center"
      bgcolor={theme.palette.background.paper}
      {...rest}
    >
      {children}
    </Stack>
  );
};

const Title = ({ namespace = 'spot', text }: { namespace?: string; text: string }) => {
  const { t } = useTranslation(namespace);

  return (
    <Typography variant="body1" color="GrayText" fontSize=".8rem" fontWeight={600}>
      {t('title.' + text)}
    </Typography>
  );
};

export const DateTile = (props: DateTileProps) => {
  const { from, previousFrom, status, previousStatus } = props;
  const { t } = useTranslation('event');
  const theme = useTheme();
  const isPostponed = status === EventStatusEnum.Postponed;
  const lastKnownDate = isPostponed && previousFrom ? previousFrom : from;
  const isDiff = !moment(from).isSame(moment(previousFrom), 'day');

  return (
    <Tile>
      <theme.icons.event />
      <Stack width="100%">
        <Title text="Date" />
        <Stack direction="row" width="100%" spacing={0} alignItems="center" justifyContent="space-between">
          <Typography
            variant="body1"
            fontSize=".8rem"
            fontWeight={600}
            sx={{
              fontWeight: isPostponed ? 400 : 'bold',
              textDecoration: isPostponed ? 'line-through' : 'none',
            }}
          >
            {moment(from).format('dddd DD MMMM YYYY')}
          </Typography>
          {isDiff && (
            <Tooltip
              enterTouchDelay={0}
              leaveTouchDelay={6000}
              title={
                <Typography variant="body2" fontSize=".8rem">
                  {t('text.originally scheduled for {{date}}', { date: moment(lastKnownDate).format('LL') })}
                </Typography>
              }
              sx={{ ml: 'auto' }}
              slotProps={{
                tooltip: {
                  sx: {
                    backgroundColor: theme.palette.grey[900],
                    color: theme.palette.common.white,
                    boxShadow: theme.shadows[3],
                    fontSize: '.9rem',
                  },
                },
              }}
            >
              <theme.icons.infoOutlined sx={{ width: 20, height: 20 }} color="warning" />
            </Tooltip>
          )}
        </Stack>
      </Stack>
    </Tile>
  );
};

export const ScheduleTile = (props: ScheduleTileProps) => {
  const { from, to, status, previousFrom } = props;
  const { t } = useTranslation('event');
  const theme = useTheme();
  const isPostponed = status === EventStatusEnum.Postponed;
  const lastKnownDate = isPostponed && previousFrom ? previousFrom : from;
  const isDiff = moment(from).format('HH:mm') !== moment(lastKnownDate).format('HH:mm');

  return (
    <Tile>
      <theme.icons.timelapse />
      <Stack width="100%">
        <Title text="Schedules" />
        <Stack direction="row" width="100%" spacing={0} alignItems="center" justifyContent="space-between">
          <Typography
            variant="body1"
            fontSize=".8rem"
            fontWeight={600}
            sx={{
              fontWeight: isPostponed ? 400 : 'bold',
              textDecoration: isPostponed ? 'line-through' : 'none',
            }}
          >
            {moment(from).format('HH:mm') + ' - ' + moment(to).format('HH:mm')}
          </Typography>
          {isDiff && (
            <Tooltip
              enterTouchDelay={0}
              leaveTouchDelay={6000}
              title={
                <Typography variant="body2" fontSize=".8rem">
                  {t('text.originally scheduled at {{time}}', {
                    time: moment(lastKnownDate).format('HH:mm'),
                  })}
                </Typography>
              }
              sx={{ ml: 'auto' }}
              slotProps={{
                tooltip: {
                  sx: {
                    backgroundColor: theme.palette.grey[900],
                    color: theme.palette.common.white,
                    boxShadow: theme.shadows[3],
                    fontSize: '.9rem',
                  },
                },
              }}
            >
              <theme.icons.infoOutlined sx={{ width: 20, height: 20 }} color="warning" />
            </Tooltip>
          )}
        </Stack>
      </Stack>
    </Tile>
  );
};

export const SpotTypeTile = (props: SpotTypeTileProps) => {
  const { types } = props;
  const theme = useTheme();
  const { t } = useTranslation('spot');

  const MAX_TYPES = 3;

  let active = Object.keys(types)
    .filter((key) => types[key as keyof SpotType] === true)
    .map((type) => t('type.' + type));

  if (active.length > MAX_TYPES) {
    active.splice(
      MAX_TYPES,
      active.length - MAX_TYPES,
      t('word.+count other', { ns: 'common', count: active.length - MAX_TYPES })
    );
  }

  return (
    <Tile>
      <theme.icons.store />
      <Stack>
        <Title text="Type" />
        <Typography variant="body1" fontSize=".8rem" fontWeight={600}>
          {active.join(', ')}
        </Typography>
      </Stack>
    </Tile>
  );
};

export const AddressTile = (props: AddressTileProps) => {
  const theme = useTheme();
  const { label } = props;
  const { t } = useTranslation('common');

  return (
    <Tile>
      <theme.icons.location />
      <Stack>
        <Title text="Direction" />
        <Typography
          variant="body1"
          fontSize=".8rem"
          color={label ? 'text.primary' : 'text.secondary'}
          fontWeight={!Boolean(label) ? 400 : 600}
          fontStyle={!Boolean(label) ? 'italic' : 'normal'}
        >
          {label || t('word.Not provided', { ns: 'common' })}
        </Typography>
      </Stack>
    </Tile>
  );
};

export const EventPriceTile = (props: EventPriceTileProps) => {
  const theme = useTheme();
  const { tickets } = props;
  const { t } = useTranslation('event');

  const curMap = {
    EUR: '€',
    USD: '$',
    GBP: '£',
  };

  const [lowestPrice] = tickets;
  const isPrice = lowestPrice?.price !== undefined && lowestPrice?.price !== null;

  return (
    <Tile>
      <theme.icons.price />
      <Stack>
        <Title text="Price" namespace="event" />
        <Typography
          variant="body1"
          fontSize=".8rem"
          color={isPrice ? 'text.primary' : 'text.secondary'}
          fontWeight={!isPrice ? 400 : 600}
          fontStyle={!isPrice ? 'italic' : 'normal'}
        >
          {isPrice
            ? t('price.from', {
                price: lowestPrice.price,
                currency: curMap[lowestPrice.currency as 'EUR' | 'USD' | 'GBP'],
              })
            : t('word.Not provided', { ns: 'common' })}
        </Typography>
      </Stack>
    </Tile>
  );
};

export const SchedulesTile = (props: SchedulesTileProps) => {
  const theme = useTheme();
  const { opening, closing } = props;
  const [open, setOpen] = React.useState(false);
  const { t } = useTranslation('spot');

  const status = getSpotOpenStatus(opening, closing, t);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const sortedOpening = [...opening].sort((a, b) => {
    // set dayOfWeek = 0 at the end
    if (a.dayOfWeek === 0) return 1;
    if (b.dayOfWeek === 0) return -1;
    return a.dayOfWeek - b.dayOfWeek;
  });

  const allSchedules = sortedOpening.map((day) => {
    const { openTime, closingTime, breakIn, breakOut } = day;
    const dayName = moment().day(day.dayOfWeek).format('dddd');
    const isToday = day.dayOfWeek === new Date().getDay();

    let dateSchedules: string | null = null;

    if (openTime && closingTime) {
      if (breakIn && breakOut) {
        dateSchedules = `${openTime.slice(0, -3)} - ${breakIn.slice(0, -3)}, ${breakOut.slice(
          0,
          -3
        )} - ${closingTime.slice(0, -3)}`;
      } else {
        dateSchedules = `${openTime.slice(0, -3)} - ${closingTime.slice(0, -3)}`;
      }
    }

    return (
      <Stack key={`schedule-${day.dayOfWeek}`} spacing={1} direction="row" justifyContent="space-between">
        <Typography
          variant="body2"
          fontSize=".8rem"
          fontWeight={isToday ? 'bold' : 'normal'}
          color={isToday ? 'textPrimary' : 'textSecondary'}
          width="75px"
        >
          {dayName.charAt(0).toUpperCase() + dayName.slice(1)}
        </Typography>
        <Typography
          variant="body2"
          fontSize=".8rem"
          fontWeight={isToday ? 'bold' : 'normal'}
          color={isToday ? 'textPrimary' : 'textSecondary'}
        >
          {dateSchedules ?? t('schedules.closed')}
        </Typography>
      </Stack>
    );
  });

  return (
    <Tile>
      <theme.icons.timelapse />
      <Stack width="100%">
        <Title text="Schedules" />
        <Stack direction="row" alignItems="center" width="100%">
          <Stack direction="row" spacing={0} alignItems="center">
            <Typography variant="body2" fontSize=".8rem" fontWeight={600} color={status.color}>
              {status.label}
            </Typography>
            <Typography variant="body2" fontSize=".8rem">
              {status.detail.length ? `, ${status.detail}` : ''}
            </Typography>
          </Stack>
          <ClickAwayListener onClickAway={handleClose}>
            <div style={{ marginLeft: 'auto' }}>
              <Tooltip
                open={open}
                enterTouchDelay={0}
                leaveTouchDelay={6000}
                title={<Stack>{allSchedules}</Stack>}
                slotProps={{
                  tooltip: {
                    sx: {
                      backgroundColor: theme.palette.grey[900],
                      color: theme.palette.common.white,
                      boxShadow: theme.shadows[3],
                      fontSize: '.9rem',
                    },
                  },
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" onClick={handleOpen}>
                  <Typography variant="body2" fontSize=".8rem">
                    {t('schedules.all schedules')}
                  </Typography>
                  <theme.icons.infoOutlined sx={{ width: 15, height: 15 }} />
                </Stack>
              </Tooltip>
            </div>
          </ClickAwayListener>
        </Stack>
      </Stack>
    </Tile>
  );
};

export const SpotPricingTile = (props: SpotPricingTileProps) => {
  const { pricing } = props;
  const theme = useTheme();
  const { t } = useTranslation('spot');

  return (
    <Tile>
      <theme.icons.price />
      <Stack width="100%">
        <Title text="Price range" />
        <Typography
          variant="body1"
          fontSize=".8rem"
          color={pricing === null ? 'text.secondary' : 'text.primary'}
          fontWeight={pricing === null ? 400 : 600}
          fontStyle={pricing === null ? 'italic' : 'normal'}
        >
          {pricing === null ? t(`word.Not provided`, { ns: 'common' }) : t(`pricing.${pricing}`)}
        </Typography>
      </Stack>
      {false && (
        <Stack width="100%">
          <Title text="Entry" />
          <Typography variant="body1" fontSize=".8rem" fontWeight={600}>
            free
          </Typography>
        </Stack>
      )}
    </Tile>
  );
};

export const SpotTile = (props: SpotTileProps) => {
  const { avatar, name, slug, ...rest } = props;
  const theme = useTheme();
  const location = useLocation();

  // mount link
  const isSearch = location.pathname.startsWith('/search/');
  const to = {
    pathname: isSearch ? `/search/spot/${slug}` : location.pathname,
    search: isSearch ? location.search : `?s=${slug}`,
  };

  return (
    <Link
      to={to}
      state={{ from: location.pathname + (location.search ?? '') }}
      preventScrollReset={true}
      style={{ width: '100%' }}
    >
      <Tile sx={{ cursor: 'pointer' }} {...rest}>
        <Avatar src={avatar} sx={{ width: theme.spacing(3), height: theme.spacing(3) }}>
          <theme.icons.store />
        </Avatar>
        <Stack>
          <Title text="Spot" />
          <Typography variant="body1" fontSize=".8rem" fontWeight={600} color="text.primary">
            {name}
          </Typography>
        </Stack>
      </Tile>
    </Link>
  );
};

export const CloseSpaceTile = (props: CloseSpaceTileProps) => {
  const { value } = props;
  const theme = useTheme();
  const { t } = useTranslation('common');

  return (
    <Tile>
      <theme.icons.spaces />
      <Stack>
        <Title text="Closed space" />
        <Typography
          variant="body1"
          fontSize=".8rem"
          color={value === null ? 'text.secondary' : 'text.primary'}
          fontWeight={value === null ? 400 : 600}
          fontStyle={value === null ? 'italic' : 'normal'}
        >
          {value || t('word.Not provided')}
        </Typography>
      </Stack>
    </Tile>
  );
};

export const CapacityTile = (props: CapacityTileProps) => {
  const { max = 'Not provided' } = props;
  const isEmpty = props.max === undefined || props.max === null;
  const theme = useTheme();
  const { t } = useTranslation('spot');

  return (
    <Tile>
      <theme.icons.people />
      <Stack>
        <Title text="Capacity" />
        <Typography
          variant="body1"
          fontSize=".8rem"
          color={isEmpty ? 'text.secondary' : 'text.primary'}
          fontWeight={isEmpty ? 400 : 600}
          fontStyle={isEmpty ? 'italic' : 'normal'}
        >
          {!isEmpty ? t('capacity.up to', { max }) : t('word.Not provided', { ns: 'common' })}
        </Typography>
      </Stack>
    </Tile>
  );
};

export const AtmosphereTile = (props: AtmosphereTileProps) => {
  const { atmosphere, ...rest } = props;
  const theme = useTheme();
  const { t } = useTranslation('spot');

  const calm = atmosphere?.calm;
  const lively = atmosphere?.lively;
  const dancing = atmosphere?.dancing;
  const isEmpty = [calm, lively, dancing].every((v) => v !== true);
  const icon = (dancing && <theme.icons.volMax />) ||
    (lively && <theme.icons.volMid />) ||
    (calm && <theme.icons.volMin />) || <theme.icons.volMid />;

  const atmospheres = getTrueKeys(atmosphere || {})
    .map((a) => t('atmosphere.' + a))
    .join(', ');

  return (
    <Tile {...rest}>
      {icon}
      <Stack>
        <Title text="Atmosphere" />
        <Typography
          variant="body1"
          fontSize=".8rem"
          color={isEmpty ? 'text.secondary' : 'text.primary'}
          fontWeight={isEmpty ? 400 : 600}
          fontStyle={isEmpty ? 'italic' : 'normal'}
        >
          {isEmpty ? t('word.Not provided', { ns: 'common' }) : atmospheres}
        </Typography>
      </Stack>
    </Tile>
  );
};

export const MusicalGenreTile = (props: MusicalGenreTileProps) => {
  const { type, ...rest } = props;
  const theme = useTheme();
  const { t } = useTranslation('spot');

  return (
    <Tile {...rest}>
      <theme.icons.music />
      <Stack>
        <Title text="Vibe" />
        <Typography
          variant="body1"
          fontSize=".8rem"
          color={!type ? 'text.secondary' : 'text.primary'}
          fontWeight={!type ? 400 : 600}
          fontStyle={!type ? 'italic' : 'normal'}
        >
          {type ? t('vibe.' + type) || t('word.Not provided', { ns: 'common' }) : '-'}
        </Typography>
      </Stack>
    </Tile>
  );
};

export default {
  Date: DateTile,
  SpotType: SpotTypeTile,
  Schedule: ScheduleTile,
  Address: AddressTile,
  EventPrice: EventPriceTile,
  SpotPricing: SpotPricingTile,
  Spot: SpotTile,
  Capacity: CapacityTile,
  CloseSpace: CloseSpaceTile,
  Atmosphere: AtmosphereTile,
  MusicalGenre: MusicalGenreTile,
  Schedules: SchedulesTile,
};
